import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import Login from "./components/Login";
import OwnerDashboard from "./components/OwnerDashboard";
import AgentDashboard from "./components/AgentDashboard";
import SuperAdminDashboard from "./components/SuperAdminDashboard";
import ImpersonationBanner from "./components/ImpersonationBanner";
import SetupPassword from "./components/SetupPassword";
import ResetPassword from "./components/ResetPassword";
import { Toaster } from "./components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with auth
export const api = axios.create({
  baseURL: API,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Portfolio Context for managing active portfolio across components
export const PortfolioContext = createContext({
  activePortfolioId: null,
  setActivePortfolioId: () => {},
  portfolios: [],
  setPortfolios: () => {},
  refreshPortfolios: () => {},
});

export const usePortfolio = () => useContext(PortfolioContext);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [originalAdminToken, setOriginalAdminToken] = useState(null);
  const [impersonatedUserEmail, setImpersonatedUserEmail] = useState(null);
  
  // Multi-portfolio state
  const [activePortfolioId, setActivePortfolioId] = useState(null);
  const [portfolios, setPortfolios] = useState([]);

  // Load portfolios when user is authenticated
  const refreshPortfolios = async () => {
    try {
      const response = await api.get('/portfolios');
      setPortfolios(response.data);
      
      // Set active portfolio from localStorage or default to first one
      const savedPortfolioId = localStorage.getItem('activePortfolioId');
      const portfolioIds = response.data.map(p => p.id);
      
      if (savedPortfolioId && portfolioIds.includes(savedPortfolioId)) {
        setActivePortfolioId(savedPortfolioId);
      } else if (portfolioIds.length > 0) {
        setActivePortfolioId(portfolioIds[0]);
        localStorage.setItem('activePortfolioId', portfolioIds[0]);
      }
    } catch (error) {
      console.error('Failed to load portfolios:', error);
    }
  };

  // Update localStorage when active portfolio changes
  const handleSetActivePortfolio = (portfolioId) => {
    setActivePortfolioId(portfolioId);
    localStorage.setItem('activePortfolioId', portfolioId);
  };

  useEffect(() => {
    checkAuth();
  }, []);

  // Load portfolios when user changes
  useEffect(() => {
    if (user && user.role !== 'super-admin') {
      refreshPortfolios();
    }
  }, [user]);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    const savedOriginalToken = localStorage.getItem('originalAdminToken');
    const savedImpersonatedEmail = localStorage.getItem('impersonatedUserEmail');
    
    if (token) {
      try {
        const response = await api.get('/auth/me');
        setUser(response.data);
        
        // Restore impersonation state if it exists
        if (savedOriginalToken && savedImpersonatedEmail) {
          setIsImpersonating(true);
          setOriginalAdminToken(savedOriginalToken);
          setImpersonatedUserEmail(savedImpersonatedEmail);
        }
      } catch (error) {
        localStorage.removeItem('token');
        localStorage.removeItem('originalAdminToken');
        localStorage.removeItem('impersonatedUserEmail');
        localStorage.removeItem('activePortfolioId');
        setUser(null);
      }
    }
    setLoading(false);
  };

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('originalAdminToken');
    localStorage.removeItem('impersonatedUserEmail');
    localStorage.removeItem('activePortfolioId');
    setUser(null);
    setIsImpersonating(false);
    setOriginalAdminToken(null);
    setImpersonatedUserEmail(null);
    setActivePortfolioId(null);
    setPortfolios([]);
  };

  const handleImpersonate = (impersonationData) => {
    // Save current admin token before switching
    const currentToken = localStorage.getItem('token');
    
    // Set up impersonation
    localStorage.setItem('originalAdminToken', currentToken);
    localStorage.setItem('token', impersonationData.access_token);
    localStorage.setItem('impersonatedUserEmail', impersonationData.user.email);
    
    setOriginalAdminToken(currentToken);
    setIsImpersonating(true);
    setImpersonatedUserEmail(impersonationData.user.email);
    setUser(impersonationData.user);
    
    // Reset portfolio state for impersonated user
    setActivePortfolioId(null);
    setPortfolios([]);
  };

  const handleExitImpersonation = async () => {
    if (originalAdminToken) {
      // Restore admin token
      localStorage.setItem('token', originalAdminToken);
      localStorage.removeItem('originalAdminToken');
      localStorage.removeItem('impersonatedUserEmail');
      
      // Re-fetch admin user data
      try {
        const response = await api.get('/auth/me');
        setUser(response.data);
      } catch (error) {
        handleLogout();
        return;
      }
      
      setIsImpersonating(false);
      setOriginalAdminToken(null);
      setImpersonatedUserEmail(null);
      setActivePortfolioId(null);
      setPortfolios([]);
    }
  };

  const getDashboardComponent = () => {
    if (!user) return <Navigate to="/login" />;
    
    // When impersonating, show the impersonated user's dashboard
    if (isImpersonating) {
      if (user.role === 'owner') {
        return <OwnerDashboard user={user} onLogout={handleLogout} />;
      } else {
        return <AgentDashboard user={user} onLogout={handleLogout} />;
      }
    }
    
    // Normal flow based on role
    switch (user.role) {
      case 'super-admin':
        return <SuperAdminDashboard user={user} onLogout={handleLogout} onImpersonate={handleImpersonate} />;
      case 'owner':
        return <OwnerDashboard user={user} onLogout={handleLogout} />;
      case 'agent':
        return <AgentDashboard user={user} onLogout={handleLogout} />;
      default:
        return <Navigate to="/login" />;
    }
  };

  // Portfolio context value
  const portfolioContextValue = {
    activePortfolioId,
    setActivePortfolioId: handleSetActivePortfolio,
    portfolios,
    setPortfolios,
    refreshPortfolios,
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <PortfolioContext.Provider value={portfolioContextValue}>
      <div className="App">
        <Toaster position="top-right" expand={false} richColors />
        
        {/* Impersonation Banner */}
        {isImpersonating && (
          <ImpersonationBanner 
            impersonatedUser={impersonatedUserEmail} 
            onExit={handleExitImpersonation} 
          />
        )}
        
        <BrowserRouter>
          <div className={isImpersonating ? 'impersonating-content' : ''}>
            <Routes>
            <Route
              path="/login"
              element={user ? <Navigate to="/" /> : <Login onLogin={handleLogin} />}
            />
            <Route
              path="/setup-password"
              element={<SetupPassword onLogin={handleLogin} />}
            />
            <Route
              path="/reset-password"
              element={<ResetPassword />}
            />
            <Route
              path="/"
              element={getDashboardComponent()}
            />
            </Routes>
          </div>
        </BrowserRouter>
      </div>
    </PortfolioContext.Provider>
  );
}

export default App;
